import { useState } from "react";
import ButtonAddNew from "src/components/atoms/button-add-new/button-add-new";
import SelectableListItem from "src/components/molecules/selectable-list-item/selectable-list-item";
import HelpText from "src/components/atoms/help-text/help-text";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface EntityListProps<T> {
	data: T[];
	selectMode?: boolean;
	helpText?: string[];
	listLabel: string;
	canAddNew?: boolean;
	onAddNewClick?: () => void;
	onViewClicked?: (item: any) => void;
	onEditClicked?: (item: any) => void;
	onDeleteClicked?: (item: any) => void;
	onItemSelected?: (item: any) => void;
	onClose?: () => void;
	renderChildren: (item: T) => React.ReactNode;
	filterText?: string;
	enableView?: boolean;
	enableEdit?: boolean;
	enableDelete?: boolean;
	recordsPerPage?: number;
}

const EntityList = <T,>({
	data,
	selectMode,
	helpText,
	canAddNew = true,
	listLabel,
	onAddNewClick,
	onViewClicked,
	onEditClicked,
	onDeleteClicked,
	onItemSelected,
	renderChildren,
	filterText = "Filter",
	enableView = true,
	enableEdit = true,
	enableDelete = true,
	recordsPerPage = 5,
}: EntityListProps<T>) => {
	const [filterValue, setFilterValue] = useState("");
	const [currentPage, setCurrentPage] = useState(1);

	const filteredData = data.filter((item) =>
		JSON.stringify(item).toLowerCase().includes(filterValue.toLowerCase())
	);

	const totalPages = Math.ceil(filteredData.length / recordsPerPage);
	const paginatedData = filteredData.slice(
		(currentPage - 1) * recordsPerPage,
		currentPage * recordsPerPage
	);

	const handleSelectItem = (item: T) => {
		onItemSelected?.(item);
	};

	const nextPage = () =>
		setCurrentPage((prev) => Math.min(prev + 1, totalPages));
	const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

	return (
		<div className="w-full overflow-auto">
			<div className="w-full">
				<div className="w-full flex items-center justify-end gap-4 pb-4">
					{!selectMode && (
						<ButtonAddNew
							onClick={() => onAddNewClick?.()}
							disabled={!canAddNew}
						/>
					)}
				</div>

				<div className="w-full flex flex-col items-center justify-center gap-2">
					{data.length > 5 && (
						<div className="w-full flex items-center justify-center pb-6">
							<InputBox
								label={filterText}
								type="text"
								value={filterValue}
								onChange={(value: string) => {
									setFilterValue(value);
									setCurrentPage(1);
								}}
								disabled={data.length === 0}
							/>
						</div>
					)}

					{paginatedData.map((item, index) => (
						<SelectableListItem
							key={index}
							item={item}
							selectMode={selectMode ?? false}
							onEditClicked={() => onEditClicked?.(item)}
							onItemSelected={() => handleSelectItem(item)}
							onDeleteClicked={() => onDeleteClicked?.(item)}
							onViewClicked={onViewClicked}
							enableView={enableView}
							enableEdit={enableEdit}
							enableDelete={enableDelete}
						>
							{renderChildren(item)}
						</SelectableListItem>
					))}
					{filteredData.length > 0 && (
						<span className="font-Poppins font-semibold text-md text-gray-800 w-full text-right">
							Total -{" "}
							<span className="font-code font-semibold text-gray-800 w-full text-right">
								{data.length}
							</span>
						</span>
					)}
					{filteredData.length === 0 && (
						<span className="font-Poppins font-semibold text-lg">
							No results
						</span>
					)}

					{totalPages > 1 && (
						<div className="flex items-center justify-center gap-4 w-full mt-4">
							<button
								onClick={prevPage}
								disabled={currentPage === 1}
								className={`font-Poppins font-semibold px-2 py-2 text-gray-800 rounded ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"}`}
							>
								<ChevronLeft size={32} />
							</button>
							<span className="font-Poppins font-semibold text-gray-800">
								Page {currentPage} of {totalPages}
							</span>
							<button
								onClick={nextPage}
								disabled={currentPage === totalPages}
								className={`font-Poppins font-semibold px-2 py-2 text-gray-800 rounded ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"}`}
							>
								<ChevronRight size={32} />
							</button>
						</div>
					)}
				</div>

				{data.length === 0 && helpText && helpText.length > 0 && (
					<HelpText help={helpText} alignment="center" />
				)}
			</div>
		</div>
	);
};

export default EntityList;
